/**
 * The audio system: one façade over the context, the mixer and the voices.
 *
 * ```
 *   flight state ─► AircraftAudioState ─┐
 *   camera       ─► ListenerAudioState ─┼─► AudioSystem.updateFrame ─► voices ─► Mixer ─► out
 *   weather      ─► EnvironmentAudioState ┘
 * ```
 *
 * `main.ts` fills three plain structs and calls one method. It does not know what a `GainNode`
 * is, and nothing here knows what a `FlightDynamicsModel` is; the structs in `state.ts` are the
 * whole interface, which is what makes a second aeroplane's sound a matter of a second
 * {@link AudioSystem} rather than a refactor.
 *
 * ## Silence is a supported state
 *
 * Constructed with a null context — no Web Audio on the platform, audio disabled by policy,
 * running under Vitest — every method here becomes a cheap no-op. The simulator is fully playable
 * in silence, `npm run perf` measures a run with the audio system present and doing nothing, and
 * no caller needs a null check.
 *
 * ## Doppler
 *
 * Web Audio removed its own Doppler support years ago (`PannerNode.dopplerFactor` is gone), which
 * turns out to be a gift: every pitched thing in this system is synthesised, so a Doppler shift
 * is a multiplication on a frequency rather than a resampling of a buffer. The listener's and the
 * aircraft's velocities are resolved along the line between them, {@link dopplerRatio} turns the
 * pair into a ratio, and that ratio multiplies the engine's fundamental, its blade-pass tone, the
 * wind's cutoff and the horn. The whole aeroplane shifts together, exactly as a real one does,
 * and a chase camera flying formation gets a ratio of exactly 1 and hears no shift at all.
 *
 * ## The budget
 *
 * CLAUDE.md's audio channel is 0.5 ms a frame, enforced by the profiler and reported by
 * `npm run perf`. The work in {@link updateFrame} is a few dozen arithmetic operations and a few
 * dozen `AudioParam` writes, most of which are dropped by `param.ts`'s redundancy filter. The
 * synthesis itself runs on the audio thread and does not touch this budget at all — which is the
 * real reason everything here is a node graph rather than a `ScriptProcessor`.
 *
 * `updateFrame(alpha)`: {@link AudioSystem.updateFrame}, once per rendered frame, from the game
 * loop's audio slot. Allocation-free; the state structs are owned by the caller and reused.
 */

import { Vector3 } from 'three';
import { clamp, clamp01 } from '../core/math.js';
import { Rng, rng as globalRng } from '../core/rng.js';
import { createAudioBank, type AudioBank } from './bank.js';
import { createAudioContext, type AudioContextHandle, type SkySimAudioContext } from './context.js';
import { dopplerRatio } from './model.js';
import { BUSES, Mixer, makeMixerState, type BusName, type MixerState } from './mixer.js';
import type { AircraftSoundConfig } from './soundSet.js';
import {
  makeAircraftAudioState,
  makeEnvironmentAudioState,
  makeListenerAudioState,
  makeWeaponsAudioState,
  type AircraftAudioState,
  type EnvironmentAudioState,
  type ListenerAudioState,
  type WeaponsAudioState,
} from './state.js';
import { AirframeVoice } from './voices/airframe.js';
import { AmbientVoice } from './voices/ambient.js';
import { GroundVoice } from './voices/ground.js';
import { OrdnanceVoice } from './voices/ordnance.js';
import { OneShotPool } from './voices/parts.js';
import { PistonEngineVoice } from './voices/pistonEngine.js';
import { SeekerVoice } from './voices/seeker.js';
import { TurbineVoice } from './voices/turbine.js';

/** The volume settings the mixer reads. A subset of `Settings`, so this module does not import it. */
export interface AudioVolumeSettings {
  masterVolume: number;
  engineVolume: number;
  windVolume: number;
  ambienceVolume: number;
  effectsVolume: number;
  muted: boolean;
}

export interface AudioSystemOptions {
  soundConfig: AircraftSoundConfig;
  /** Injected by the tests. Omitted in the app, where the platform constructor is used. */
  createContext?: () => SkySimAudioContext;
  /** Where the unlock listeners go. `null` disables them, for tests. */
  gestureTarget?: EventTarget | null;
  /** Forked from the global RNG so audio never perturbs anyone else's sequence. */
  rng?: Rng;
}

/** Debug triggers for the voices nothing drives yet. Reachable on `__SKYSIM__.audio.debug`. */
export interface AudioDebugTriggers {
  cannon(): void;
  missile(): void;
  explosion(distanceM?: number): void;
  /** Command the turbine's spool, 0…1, and its nozzle stage, 0…1. Persists until called again. */
  turbine(n1: number, afterburner?: number): void;
  /** Fire the touchdown pair at a chosen sink rate [m/s] and ground speed [m/s]. */
  touchdown(sinkRateMps?: number, groundSpeedMps?: number): void;
  click(): void;
}

/**
 * How often the cannon's per-shot transient is actually triggered [Hz].
 *
 * Well above the ~30 Hz at which a train of transients fuses into a pitch, so nothing audible is
 * lost, and well below the 100 Hz a 6000 rpm gun would otherwise ask for. See
 * {@link AudioSystem.fireCannon}.
 */
const CANNON_TRIGGER_HZ = 25;

const listenerVelocity = /* #__PURE__ */ new Vector3();
const toListener = /* #__PURE__ */ new Vector3();

export class AudioSystem {
  /** Filled by the caller each frame; owned here so nothing allocates one per frame. */
  readonly aircraft: AircraftAudioState = makeAircraftAudioState();
  readonly listener: ListenerAudioState = makeListenerAudioState();
  readonly environment: EnvironmentAudioState = makeEnvironmentAudioState();
  readonly weapons: WeaponsAudioState = makeWeaponsAudioState();

  private readonly handle: AudioContextHandle;
  private readonly context: SkySimAudioContext | null;
  private readonly bank: AudioBank | null = null;
  private readonly mixer: Mixer | null = null;

  private readonly piston: PistonEngineVoice | null = null;
  private readonly airframe: AirframeVoice | null = null;
  private readonly ground: GroundVoice | null = null;
  private readonly ambient: AmbientVoice | null = null;
  private readonly turbine: TurbineVoice | null = null;
  private readonly ordnance: OrdnanceVoice | null = null;
  private readonly seekerVoice: SeekerVoice | null = null;
  private readonly uiShots: OneShotPool | null = null;

  private readonly mix: MixerState = makeMixerState();
  private readonly soundConfig: AircraftSoundConfig;

  /**
   * Turbine command.
   *
   * Written every frame from {@link AircraftAudioState} when the aeroplane actually has a turbine,
   * and left alone otherwise so `debug.turbine()` still works on a piston aircraft. Before M6
   * nothing drove it at all and the debug trigger was the only writer; the `engineKind` test below
   * is what handed it over to the flight model without breaking that.
   */
  private turbineN1 = 0;
  private turbineAfterburner = 0;
  /** Context time of the last cannon transient [s] — see {@link fireCannon}. */
  private lastCannonS = Number.NEGATIVE_INFINITY;

  constructor(options: AudioSystemOptions) {
    this.soundConfig = options.soundConfig;
    this.handle = createAudioContext({
      ...(options.createContext === undefined ? {} : { create: options.createContext }),
      ...(options.gestureTarget === undefined ? {} : { target: options.gestureTarget }),
    });
    this.context = this.handle.context;
    if (this.context === null) return;

    const rng = options.rng ?? globalRng.fork();
    this.bank = createAudioBank(this.context, rng);
    this.mixer = new Mixer(this.context);

    this.piston = new PistonEngineVoice(
      this.context,
      this.bank,
      this.mixer.destination('engine'),
      this.soundConfig,
    );
    this.turbine = new TurbineVoice(this.context, this.bank, this.mixer.destination('engine'));
    this.airframe = new AirframeVoice(
      this.context,
      this.bank,
      this.mixer.destination('airframe'),
      this.soundConfig,
    );
    this.ground = new GroundVoice(
      this.context,
      this.bank,
      this.mixer.destination('airframe'),
      this.soundConfig,
      rng,
    );
    this.ambient = new AmbientVoice(
      this.context,
      this.bank,
      this.mixer.destination('environment'),
      rng,
    );
    this.ordnance = new OrdnanceVoice(this.context, this.bank, this.mixer.destination('weapons'), rng);
    this.seekerVoice = new SeekerVoice(this.context, this.mixer.destination('weapons'));
    this.uiShots = new OneShotPool(this.context, this.mixer.destination('ui'), rng, 8);
  }

  /** True once the context is running and something could actually be heard. */
  get running(): boolean {
    return this.handle.running;
  }

  /** True when the platform gave us no audio at all; everything is a no-op. */
  get silent(): boolean {
    return this.context === null;
  }

  /** The mixer, for the tests and the debug handle. Null when there is no audio. */
  get busses(): Mixer | null {
    return this.mixer;
  }

  /** Push the volume sliders. Called on every settings change, not per frame. */
  applySettings(settings: AudioVolumeSettings): void {
    const volumes = this.mix.volumes as Record<BusName, number>;
    volumes.engine = settings.engineVolume;
    volumes.airframe = settings.windVolume;
    volumes.environment = settings.ambienceVolume;
    volumes.weapons = settings.effectsVolume;
    volumes.ui = settings.effectsVolume;
    this.mix.masterVolume = settings.masterVolume;
    this.mix.muted = settings.muted;
  }

  /**
   * Resolve the listener's motion relative to the aircraft into a Doppler ratio and a bearing.
   *
   * Both are render-space vectors, which is fine and in fact necessary: Doppler depends only on
   * *relative* velocity along the line of sight, and a floating-origin rebase translates both
   * endpoints identically and leaves that line unchanged.
   *
   * Writes {@link listener}`.dopplerRatio`, `.distanceM` and `.pan`. Borrows two module scratch
   * vectors and returns nothing.
   */
  observe(
    aircraftPosition: Vector3,
    aircraftVelocity: Vector3,
    listenerPosition: Vector3,
    listenerPreviousPosition: Vector3,
    listenerRight: Vector3,
    frameDtS: number,
  ): void {
    toListener.subVectors(listenerPosition, aircraftPosition);
    const distance = toListener.length();
    this.listener.distanceM = distance;

    if (distance < 1e-3) {
      this.listener.dopplerRatio = 1;
      this.listener.pan = 0;
      return;
    }
    toListener.multiplyScalar(1 / distance);

    /*
     * The camera has no velocity of its own to publish — it is a spring-damped rig, not a body —
     * so it is differenced. Guarded on the frame delta because a paused frame or the first frame
     * after a tab regains focus can produce a delta of zero or of a second, and either one turns
     * into a nonsense velocity and an audible chirp.
     */
    if (frameDtS > 1e-4 && frameDtS < 0.25) {
      listenerVelocity.subVectors(listenerPosition, listenerPreviousPosition).divideScalar(frameDtS);
    } else {
      listenerVelocity.set(0, 0, 0);
    }

    // Both components are "toward the other party", so the same sign convention works for both.
    const closingSource = aircraftVelocity.dot(toListener);
    const closingListener = -listenerVelocity.dot(toListener);
    this.listener.dopplerRatio = dopplerRatio(
      closingSource,
      closingListener,
      this.aircraft.speedOfSoundMps,
    );

    // Pan by where the aeroplane is across the listener's own right axis, softened: a hard
    // stereo image on a laptop's speakers is worse than a centred one, and headphones do not
    // need the help.
    this.listener.pan = clamp(-toListener.dot(listenerRight) * 0.7, -1, 1);
  }

  /**
   * One frame of sound.
   *
   * @param frameDtS Real seconds since the last frame — drives the bird schedule and the
   *                 turbine's spool lag, both of which are wall-clock rather than sim-clock
   *                 processes and must keep running while the simulation is paused.
   */
  updateFrame(frameDtS: number): void {
    if (this.mixer === null) return;

    const listener = this.listener;
    const ratio = listener.dopplerRatio;

    /*
     * A turbine's spool comes from the aeroplane, not from a debug call.
     *
     * `rpm` on a jet is N2 per cent — see the note on `Powerplant` — so it is normalised here
     * rather than in the voice, which has no business knowing what units an engine reports in.
     */
    if (this.soundConfig.engineKind === 'turbine') {
      this.turbineN1 = clamp01(this.aircraft.rpm / 100);
      this.turbineAfterburner = clamp01(this.aircraft.afterburnerFraction);
    }

    this.piston?.updateFrame(this.aircraft, ratio);
    this.airframe?.updateFrame(this.aircraft, ratio);
    this.ground?.updateFrame(this.aircraft);
    this.ambient?.updateFrame(this.aircraft, this.environment, frameDtS);
    this.turbine?.updateFrame(
      this.turbineN1,
      this.turbineAfterburner,
      frameDtS,
      ratio,
      this.soundConfig.engineKind === 'turbine' || this.turbineN1 > 0,
    );

    this.seekerVoice?.updateFrame(
      this.weapons.seekerTone,
      this.weapons.seekerLocked,
      this.weapons.armed ? 1 : 0,
    );

    this.mix.cockpitFraction = clamp01(listener.cockpitFraction);
    this.mix.cloudiness = clamp01(listener.cloudiness);
    this.mix.distanceM = listener.distanceM;
    this.mix.pan = listener.pan;
    this.mix.activity = clamp01(listener.activity);
    this.mixer.updateFrame(this.mix);
  }

  // --- weapons -------------------------------------------------------------------------------

  /**
   * One round leaving the gun.
   *
   * Called once per round fired, which at 6000 rpm is a hundred times a second — and **not
   * played** a hundred times a second, deliberately.
   *
   * ## Why the rate is capped
   *
   * Two reasons, and they happen to agree.
   *
   * The psychoacoustic one: shots 10 ms apart are below the ear's ability to resolve them as
   * separate events. Above roughly 30 Hz a train of transients stops being a sequence of bangs
   * and fuses into a *pitch* — which is exactly what a 6000 rpm cannon sounds like, a 100 Hz
   * buzz rather than a hundred cracks. Triggering every round therefore adds no information; the
   * texture comes from the pitch jitter and the room delay in `voices/ordnance.ts`.
   *
   * The measured one: `OneShotPool.play` must build an `AudioBufferSourceNode` per shot, because
   * Web Audio source nodes are single-use and cannot be pooled. With a gain node and an `onended`
   * closure that is three objects per round, three hundred a second, and it put the `npm run perf`
   * heap delta over its 8 MB budget on the canyon run — a load-independent budget, so it failed
   * honestly rather than being a symptom of a busy host.
   *
   * Capping at {@link CANNON_TRIGGER_HZ} cuts that by three quarters and sounds the same.
   */
  fireCannon(): void {
    if (this.context === null) return;
    const now = this.context.currentTime;
    if (now - this.lastCannonS < 1 / CANNON_TRIGGER_HZ) return;
    this.lastCannonS = now;
    this.ordnance?.cannon();
  }

  /** A missile leaving the rail. */
  launchMissile(): void {
    this.ordnance?.missile();
  }

  /** Something exploding, `distanceM` away — the range sets both level and tone. */
  explosion(distanceM: number): void {
    this.ordnance?.explosion(distanceM);
  }

  /** A switch, a menu button, a detent. The one sound that is not in the world. */
  click(gain = 0.35): void {
    if (this.bank === null || this.uiShots === null) return;
    this.uiShots.play(this.bank.click, gain, 1, 0.08);
  }

  /** True while the stall horn is sounding. */
  get stallHornSounding(): boolean {
    return this.airframe?.hornSounding ?? false;
  }

  readonly debug: AudioDebugTriggers = {
    cannon: () => this.ordnance?.cannon(),
    missile: () => this.ordnance?.missile(),
    explosion: (distanceM = 80) => this.ordnance?.explosion(distanceM),
    turbine: (n1: number, afterburner = 0) => {
      this.turbineN1 = clamp01(n1);
      this.turbineAfterburner = clamp01(afterburner);
    },
    touchdown: (sinkRateMps = 2, groundSpeedMps = 30) =>
      this.ground?.debugTouchdown(sinkRateMps, groundSpeedMps),
    click: () => this.click(),
  };

  dispose(): void {
    this.handle.dispose();
    this.piston?.dispose();
    this.airframe?.dispose();
    this.ground?.dispose();
    this.ambient?.dispose();
    this.turbine?.dispose();
    this.ordnance?.dispose();
    this.seekerVoice?.dispose();
    this.mixer?.dispose();
    void this.context?.close().catch(() => {
      /* already closed */
    });
  }
}

/** Re-exported so callers need one import to name a bus. */
export { BUSES, type BusName };
